import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SPEND = 40000, CASES = 12, WASTE = 20, LOOP = 11000;
const easeOut = (x) => 1 - Math.pow(1 - x, 3);
const fmt = (n) => "$" + Math.round(n).toLocaleString();

export default function RoiShowcase() {
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf, start;
    const tick = (ts) => {
      if (!start) start = ts;
      setP(((ts - start) % LOOP) / LOOP);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const ramp = easeOut(Math.min(p / 0.42, 1));
  const spend = SPEND * ramp;
  const cases = CASES * ramp;
  const waste = WASTE * ramp;
  const costPerCase = cases > 0.5 ? spend / cases : 0;
  const savings = spend * (waste / 100);
  const showResults = p > 0.44;
  const showCta = p > 0.8;

  const sliders = [
    { label: "Monthly marketing spend", val: fmt(spend), pct: (spend / 120000) * 100 },
    { label: "Signed cases / month", val: Math.round(cases), pct: (cases / 30) * 100 },
    { label: "Est. unverified / wasted spend", val: `${Math.round(waste)}%`, pct: (waste / 40) * 100 },
  ];

  return (
    <div className="min-h-screen bg-[#0A1B2E] text-white font-body flex flex-col items-center justify-center px-6 overflow-hidden" data-testid="roi-showcase">
      <div className="absolute top-8 left-8 flex items-center gap-2.5 font-body font-bold text-xl tracking-tight">
        <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-7 w-auto" />
        <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
      </div>

      <div className="w-full max-w-5xl">
        <motion.p
          key={showCta ? "cta-eyebrow" : "eyebrow"}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="font-mono-data text-xs uppercase tracking-[0.25em] text-[#D5A44A] mb-4"
        >
          Retained-case ROI calculator
        </motion.p>

        <AnimatePresence mode="wait">
          {!showCta ? (
            <motion.h1
              key="headline"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="font-display text-4xl sm:text-5xl font-light leading-[1.05] mb-10"
            >
              How much of your spend becomes <span className="italic text-[#D5A44A]">signed cases</span>?
            </motion.h1>
          ) : (
            <motion.h1
              key="cta"
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              className="font-display text-4xl sm:text-6xl font-light leading-[1.05] mb-10"
            >
              Your agencies report the activity.<br />
              <span className="text-[#D5A44A]">RetainProof measures the outcome.</span>
            </motion.h1>
          )}
        </AnimatePresence>

        {!showCta && (
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* Sliders */}
            <div className="border border-white/10 bg-[#12324D] p-7 space-y-6">
              {sliders.map((s) => (
                <div key={s.label}>
                  <div className="flex justify-between text-sm text-zinc-400 mb-2">
                    <span>{s.label}</span>
                    <span className="font-mono-data text-white text-base">{s.val}</span>
                  </div>
                  <div className="h-2 bg-black/40 relative">
                    <div className="h-2 bg-[#D5A44A] absolute left-0 top-0" style={{ width: `${Math.min(s.pct, 100)}%` }} />
                    <div className="h-4 w-4 rounded-full bg-[#D5A44A] absolute -top-1 -ml-2 shadow" style={{ left: `${Math.min(s.pct, 100)}%` }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Results */}
            <AnimatePresence>
              {showResults && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                  className="grid grid-cols-1 gap-4"
                >
                  <div className="border border-white/10 bg-black/30 p-6">
                    <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">Cost / retained case</p>
                    <p className="font-display text-5xl text-white mt-2">{fmt(costPerCase)}</p>
                  </div>
                  <div className="border border-[#D5A44A]/40 bg-[#D5A44A]/10 p-6">
                    <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#D5A44A]">Potential monthly savings</p>
                    <p className="font-display text-5xl text-[#D5A44A] mt-2">{fmt(savings)}</p>
                    <p className="text-xs text-zinc-400 mt-2">Up to <span className="text-white">{fmt(savings * 12)}/yr</span> in unverified spend to recover.</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {showCta && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
            className="text-lg text-zinc-400">
            Independent marketing accountability for law firms.
          </motion.p>
        )}
      </div>
    </div>
  );
}
